import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { type JSX, type ReactNode, type Ref, useCallback } from "react";

type OnboardingConnectSurfaceProps = {
  connectBody: ReactNode;
  firstCallBody: ReactNode | null;
  onAdvanceToGetToWork: () => void;
  showFirstCall: boolean;
  showGetToWork: boolean;
};

const stepNumberBaseClassName =
  "flex size-6 shrink-0 items-center justify-center rounded-full font-medium font-sans text-xs tabular-nums transition-colors duration-300";

function StepHeading({
  step,
  title,
  active,
  headingId,
}: {
  step: 1 | 2;
  title: string;
  active: boolean;
  headingId: string;
}): JSX.Element {
  const stepNumberStateClassName = active
    ? "border-2 border-brand-text text-brand-text"
    : "border border-border text-muted-foreground/40";
  const titleColorClassName = active
    ? "text-foreground"
    : "text-muted-foreground";

  return (
    <>
      <span
        className={`${stepNumberBaseClassName} ${stepNumberStateClassName}`}
      >
        {step}
      </span>
      <h2
        className={`m-0 font-serif text-xl leading-none tracking-tight transition-colors ${titleColorClassName}`}
        id={headingId}
      >
        {title}
      </h2>
    </>
  );
}

function StaticStepSection({
  step,
  title,
  children,
  sectionRef,
}: {
  step: 1 | 2;
  title: string;
  children: ReactNode;
  sectionRef?: Ref<HTMLElement>;
}): JSX.Element {
  const headingId = `onboarding-connect-step-${step}-heading`;
  return (
    <section aria-labelledby={headingId} ref={sectionRef} tabIndex={-1}>
      <div className="flex h-7 items-center gap-3">
        <StepHeading active headingId={headingId} step={step} title={title} />
      </div>
      <div className="pt-4">{children}</div>
    </section>
  );
}

export function OnboardingConnectSurface({
  connectBody,
  firstCallBody,
  onAdvanceToGetToWork,
  showFirstCall,
  showGetToWork,
}: OnboardingConnectSurfaceProps): JSX.Element {
  const canShowGetToWork = showFirstCall && firstCallBody !== null;
  const showGetToWorkSection = canShowGetToWork && showGetToWork;

  const revealGetToWorkSection = useCallback((node: HTMLElement | null) => {
    if (!node) {
      return;
    }
    // Move keyboard / screen-reader focus to the freshly revealed step.
    // preventScroll lets scrollIntoView own the motion (smooth via CSS), and
    // block: "nearest" only scrolls when the section isn't already in view.
    node.focus({ preventScroll: true });
    node.scrollIntoView({ block: "nearest" });
  }, []);

  return (
    <div className="flex flex-col">
      <StaticStepSection step={1} title="Connect">
        {connectBody}
        {canShowGetToWork && !showGetToWork ? (
          <div className="pt-4">
            <Button onClick={onAdvanceToGetToWork} type="button">
              Next
            </Button>
          </div>
        ) : null}
      </StaticStepSection>

      {showGetToWorkSection ? (
        <div className="border-border border-t pt-6">
          <StaticStepSection
            sectionRef={revealGetToWorkSection}
            step={2}
            title="Get to work"
          >
            {firstCallBody}
          </StaticStepSection>
        </div>
      ) : null}
    </div>
  );
}
